import { useQuery } from '@apollo/client';
import { useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { defaultPageInfo } from 'lib/enums/pageInfo';
import { PageInfo } from 'lib/types/pageInfo';
import { CareTeam } from 'generated/graphql';
import { GET_PATIENT_GROUPS_PAGINATION } from 'pagesAdmin/SettingsPage/gqlSchemes/patientGroupPagination';

type GetPatientGroupsPaginationVariables = {
  filter?: PatientGroupsFilter;
  page?: number;
  perPage?: number;
};

export type PatientGroupItem = {
  id: string;
  title: string;
  careTeamId: string;
  careTeam: CareTeam;
  patientIds: string[];
  patientsTotalCount: number;
};

export type PatientGroupsPaginationResponse = {
  items: PatientGroupItem[];
  pageInfo: PageInfo;
};

export type PatientGroupsFilter = {
  title?: string;
  careTeamId?: string;
};

const useGetPatientGroupsForAutocomplete = (
  { filter, page = 0, perPage = 10 }: GetPatientGroupsPaginationVariables = {
    filter: {},
  }
) => {
  const [items, setItems] = useState<PatientGroupsPaginationResponse['items']>(
    []
  );
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPageInfo);
  const [search, setSearch] = useState<string>('');
  const [debouncedFilter] = useDebounce(search, 300);

  const { loading, fetchMore } = useQuery<
    { patientGroup: { patientGroupsPage: PatientGroupsPaginationResponse } },
    GetPatientGroupsPaginationVariables
  >(GET_PATIENT_GROUPS_PAGINATION, {
    variables: { filter: { title: debouncedFilter, ...filter }, page, perPage },
    onCompleted: (queryData) => {
      if (queryData?.patientGroup.patientGroupsPage) {
        setItems(queryData.patientGroup.patientGroupsPage.items);
        setPageInfo(queryData.patientGroup.patientGroupsPage.pageInfo);
      }
    },
  });

  const loadMore = useCallback(() => {
    if (pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          filter: debouncedFilter,
          page: pageInfo.page + 1,
          perPage,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;
          const newItems = fetchMoreResult.patientGroup.patientGroupsPage.items;
          const newPageInfo =
            fetchMoreResult.patientGroup.patientGroupsPage.pageInfo;

          setItems((prevItems) => [...prevItems, ...newItems]);
          setPageInfo(newPageInfo);

          return fetchMoreResult;
        },
      });
    }
  }, [fetchMore, debouncedFilter, pageInfo, perPage]);

  return {
    patientGroups: items,
    pageInfo,
    loading,
    loadMore,
    setSearch,
  };
};

export default useGetPatientGroupsForAutocomplete;
